import { Controller } from "@hotwired/stimulus"
import {
  saveScheduleEntriesCache,
  getScheduleEntriesForDate,
  getScheduleEntriesForWorker
} from "../../pwa/schedule_entries_cache"
import {
  saveAttendancesCache,
  getAttendancesForDate,
  getPerformancesForWorker
} from "../../pwa/attendances_cache"
import {
  cloneRowFromTemplate,
  prefillRowFromScheduleEntry
} from "../../pwa/performance_row_builder"

// Connects to data-controller="attandances--schedule-hydrator"
//
// Responsibilities:
//   1. On connect when online: refresh the IDB cache from the embedded weekly payload.
//   2. On connect when the rendered shell is stale (server-rendered date != client today),
//      clear existing performance rows in the individual & group forms and re-render them
//      from the IDB cache for today's date.
export default class extends Controller {
  static values = {
    serverDate: String,
    currentWorkerId: String
  }

  async connect() {
    const embedded = this.#readEmbeddedPayload()
    if (embedded) {
      await saveScheduleEntriesCache(embedded)
    }

    const embeddedAttendances = this.#readEmbeddedAttendances()
    if (embeddedAttendances) {
      await saveAttendancesCache(embeddedAttendances)
    }

    const clientToday = this.#todayIso()
    const serverDate = this.serverDateValue

    if (!serverDate || serverDate === clientToday) {
      return
    }

    await this.#hydrateIndividualForm(clientToday)
    await this.#hydrateGroupPerformances(clientToday)
  }

  #readEmbeddedPayload() {
    return this.#readEmbeddedJson("weekly-schedule-entries-data")
  }

  #readEmbeddedAttendances() {
    return this.#readEmbeddedJson("weekly-attendances-data")
  }

  #readEmbeddedJson(elementId) {
    const node = document.getElementById(elementId)
    if (!node) return null
    try {
      const parsed = JSON.parse(node.textContent || "{}")
      return Object.keys(parsed).length === 0 ? null : parsed
    } catch (err) {
      return null
    }
  }

  async #hydrateIndividualForm(today) {
    if (!this.currentWorkerIdValue) {
      return
    }
    const rows = await this.#rowsForWorker(today, this.currentWorkerIdValue)

    const individualForm = this.element.querySelector("#work-confirm-form")
    if (!individualForm) {
      return
    }

    this.#repopulateContainer(individualForm, rows, "individual")
  }

  async #hydrateGroupPerformances(today) {
    const scheduleByWorker = await getScheduleEntriesForDate(today)
    const attendanceByWorker = await getAttendancesForDate(today)
    if (!scheduleByWorker && !attendanceByWorker) return

    const groupBlocks = this.element.querySelectorAll(".group-worker-block[data-worker-id]")
    groupBlocks.forEach(block => {
      const workerId = block.dataset.workerId
      const saved = attendanceByWorker?.[String(workerId)]?.performances
      const rows = (saved && saved.length > 0)
        ? saved
        : (scheduleByWorker?.[String(workerId)] || [])
      this.#repopulateContainer(block, rows, `group worker=${workerId}`)
    })
  }

  // Saved performances take priority over the schedule plan: if the worker
  // already saved work for this date, show that; otherwise fall back to the
  // scheduled entries so the form is still pre-filled offline.
  async #rowsForWorker(today, workerId) {
    const saved = await getPerformancesForWorker(today, workerId)
    if (saved && saved.length > 0) return saved
    return getScheduleEntriesForWorker(today, workerId)
  }

  #repopulateContainer(scope, entries, label) {
    const container = scope.querySelector(
      '[data-attandances--work-edit-target="performancesContainer"]'
    )
    const template = scope.querySelector(
      '[data-attandances--work-edit-target="performanceTemplate"]'
    )
    if (!container || !template) {
      return
    }

    const beforeCount = container.children.length
    container.replaceChildren()

    if (!entries || entries.length === 0) {
      return
    }

    const emptyPlaceholder = scope.querySelector("[data-no-performances]")
    if (emptyPlaceholder) emptyPlaceholder.remove()

    let appended = 0
    entries.forEach(entry => {
      const row = cloneRowFromTemplate(template)
      if (!row) return
      prefillRowFromScheduleEntry(row, entry)
      container.appendChild(row)
      appended++
    })
    console.log(`[schedule_hydrator] ${label}: cleared ${beforeCount} → appended ${appended} rows`)
  }

  #todayIso() {
    const d = new Date()
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
  }
}
